import { BRAND } from "../../brand";
import { Cpu, Bot, GitBranch, ShieldCheck } from "lucide-react";

const features = [
  { icon: Cpu, title: "IA conversacional entrenada", desc: "Agentes con tu tono, tu catálogo y tus reglas comerciales." },
  { icon: Bot, title: "Calificación automática", desc: "El sistema descarta curiosos y eleva los leads que sí compran." },
  { icon: GitBranch, title: "Flujos omnicanal", desc: "Misma conversación en WhatsApp, IG, FB, web y email." },
  { icon: ShieldCheck, title: "Cero leads perdidos", desc: "Respuesta < 1 minuto, 24/7, todos los días del año." },
];

export default function AIAutomation() {
  return (
    <section id="ai" data-testid="automation-section" className="relative py-24 sm:py-32 overflow-hidden">
      <img
        src={BRAND.images.aiBackground}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/70 to-[#050505]" />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="eyebrow mb-5">Automatización con IA</div>
            <h2 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.04]">
              Una <span className="text-gradient">IA</span> que vende, califica y escala por ti.
            </h2>
            <p className="mt-6 text-zinc-400 text-lg max-w-xl">
              No hablamos de bots básicos. Hablamos de agentes con IA conectados a tu CRM,
              tu publicidad y tu equipo comercial — operando 24/7 sin caer en la mediocridad de un chatbot genérico.
            </p>

            <div className="mt-9 grid sm:grid-cols-2 gap-3">
              {features.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="grad-border p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg glass grid place-items-center text-[#ff8a00] shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-display text-white font-semibold text-[15px]">{title}</div>
                      <p className="mt-1 text-zinc-400 text-sm leading-relaxed">{desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mock chat panel */}
          <div className="lg:col-span-6 reveal">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-[#ff3b1f]/25 to-transparent blur-3xl" />
              <div className="relative grad-border p-6 sm:p-8">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-brand grid place-items-center text-black font-display font-bold">D</div>
                    <div>
                      <div className="text-white font-display font-semibold text-sm">Daos AI Agent</div>
                      <div className="text-[11px] text-[#25D366] flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" /> En línea
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">WhatsApp</span>
                </div>

                <div className="space-y-3">
                  <Bubble side="in">Hola, vi su anuncio. ¿Tienen demo del software?</Bubble>
                  <Bubble side="out">¡Hola! 👋 Claro, te ayudo. ¿Cuántos usuarios maneja tu equipo y en qué país operan?</Bubble>
                  <Bubble side="in">Somos 35 personas, en Guatemala.</Bubble>
                  <Bubble side="out">Perfecto. Te dejo agendada una demo para mañana 10:30 AM con un especialista. ¿Confirmamos?</Bubble>
                  <Bubble side="in">Sí, confirmado.</Bubble>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                  <Tag>Calificó lead</Tag>
                  <Tag>Agendó demo</Tag>
                  <Tag>Notificó CRM</Tag>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bubble({ side, children }) {
  const isOut = side === "out";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-snug ${
        isOut
          ? "bg-gradient-brand text-black rounded-br-sm"
          : "bg-white/[0.06] border border-white/10 text-zinc-200 rounded-bl-sm"
      }`}>
        {children}
      </div>
    </div>
  );
}

function Tag({ children }) {
  return (
    <div className="text-[11px] tracking-wider uppercase glass rounded-lg py-2 text-[#ff8a00] font-display font-semibold">
      {children}
    </div>
  );
}
